import { Actor, HttpAgent, type ActorSubclass } from "@dfinity/agent";
import type { Identity } from "@dfinity/agent";

type InterfaceFactory = Parameters<typeof Actor.createActor>[0];

const indexAdminIdl: InterfaceFactory = ({ IDL }) => {
  const IndexError = IDL.Variant({
    Anonymous: IDL.Null,
    AlreadyCreating: IDL.Null,
    AlreadyStoreUser: IDL.Null,
    InvalidInput: IDL.Text,
    NotFound: IDL.Null,
    NotAuthorized: IDL.Null,
    NotOwner: IDL.Null,
    FeeConfigError: IDL.Text,
    CanisterError: IDL.Text,
  });
  const Admin = IDL.Record({
    id: IDL.Principal,
    name: IDL.Text,
  });
  const AdminAccess = IDL.Record({
    isInstaller: IDL.Bool,
    admin: IDL.Opt(Admin),
  });
  const MpesaShortcode = IDL.Variant({
    Till: IDL.Text,
    Paybill: IDL.Record({ number: IDL.Text, account: IDL.Text }),
  });
  const ShopRecord = IDL.Record({
    id: IDL.Principal,
    idx: IDL.Nat,
    name: IDL.Text,
    contacts: IDL.Opt(IDL.Text),
    address: IDL.Opt(IDL.Text),
    owner: IDL.Principal,
    accountNumber: IDL.Text,
    created: IDL.Int,
    initArgBlob: IDL.Vec(IDL.Nat8),
    mpesaShortcode: IDL.Opt(MpesaShortcode),
  });
  const ShopStats = IDL.Record({
    id: IDL.Principal,
    totalSales: IDL.Nat,
    totalSalesKes: IDL.Nat,
    totalProducts: IDL.Nat,
    totalStoreUsers: IDL.Nat,
    salesAvg: IDL.Nat,
    dailyAvg: IDL.Nat,
  });
  const PendingShopStatus = IDL.Variant({
    Pending: IDL.Null,
    AwaitingApproval: IDL.Null,
  });
  const PendingShop = IDL.Record({
    idx: IDL.Nat,
    name: IDL.Text,
    contacts: IDL.Opt(IDL.Text),
    address: IDL.Opt(IDL.Text),
    owner: IDL.Principal,
    accountNumber: IDL.Text,
    createdAt: IDL.Int,
    status: PendingShopStatus,
    registrationCode: IDL.Opt(IDL.Text),
    mpesaRef: IDL.Opt(IDL.Text),
  });

  const AdminAccessResult = IDL.Variant({ ok: AdminAccess, err: IndexError });
  const AdminResult = IDL.Variant({ ok: Admin, err: IndexError });
  const AdminsResult = IDL.Variant({ ok: IDL.Vec(Admin), err: IndexError });
  const ShopsResult = IDL.Variant({ ok: IDL.Vec(ShopRecord), err: IndexError });
  const ShopStatsResult = IDL.Variant({ ok: ShopStats, err: IndexError });
  const EInvoiceStatus = IDL.Variant({
    NotSubmitted: IDL.Null,
    Pending: IDL.Null,
    Completed: IDL.Null,
    Failed: IDL.Null,
  });
  const EInvoiceRecord = IDL.Record({
    saleId: IDL.Nat,
    status: EInvoiceStatus,
    traderInvoiceNumber: IDL.Text,
    digiTaxSaleId: IDL.Opt(IDL.Text),
    etimsUrl: IDL.Opt(IDL.Text),
    offlineUrl: IDL.Opt(IDL.Text),
    receiptNumber: IDL.Opt(IDL.Text),
    invoiceNumber: IDL.Opt(IDL.Text),
    serialNumber: IDL.Opt(IDL.Text),
    receiptSignature: IDL.Opt(IDL.Text),
    lastError: IDL.Opt(IDL.Text),
    retryCount: IDL.Nat,
    submittedAt: IDL.Opt(IDL.Int),
    lastAttemptAt: IDL.Opt(IDL.Int),
    completedAt: IDL.Opt(IDL.Int),
    retryable: IDL.Bool,
  });
  const EInvoiceRecordResult = IDL.Variant({
    ok: IDL.Opt(EInvoiceRecord),
    err: IndexError,
  });
  const PendingShopsResult = IDL.Variant({
    ok: IDL.Vec(PendingShop),
    err: IndexError,
  });
  const ShopRecordResult = IDL.Variant({ ok: ShopRecord, err: IndexError });
  const FreeRegistrationCode = IDL.Record({
    code: IDL.Text,
    created: IDL.Int,
    expiresIn: IDL.Int,
    redeemed: IDL.Bool,
  });
  const FreeSubscriptionCode = IDL.Record({
    code: IDL.Text,
    created: IDL.Int,
    expiresIn: IDL.Int,
    redeemed: IDL.Bool,
    durationDays: IDL.Nat,
  });
  const NewFreeRegistrationCode = IDL.Record({
    code: IDL.Text,
    validDays: IDL.Nat,
  });
  const NewFreeSubscriptionCode = IDL.Record({
    code: IDL.Text,
    validDays: IDL.Nat,
    durationDays: IDL.Nat,
  });
  const FreeRegistrationCodeResult = IDL.Variant({
    ok: FreeRegistrationCode,
    err: IndexError,
  });
  const FreeRegistrationCodesResult = IDL.Variant({
    ok: IDL.Vec(FreeRegistrationCode),
    err: IndexError,
  });
  const FreeSubscriptionCodeResult = IDL.Variant({
    ok: FreeSubscriptionCode,
    err: IndexError,
  });
  const FreeSubscriptionCodesResult = IDL.Variant({
    ok: IDL.Vec(FreeSubscriptionCode),
    err: IndexError,
  });
  const ShopRegistration = IDL.Record({
    id: IDL.Nat,
    shopNumber: IDL.Nat,
    created: IDL.Int,
    registrationCode: IDL.Opt(IDL.Text),
    mpesaRef: IDL.Opt(IDL.Text),
  });
  const ShopRegistrationsPageResult = IDL.Variant({
    ok: IDL.Record({ items: IDL.Vec(ShopRegistration), nextCursor: IDL.Opt(IDL.Text) }),
    err: IndexError,
  });
  const ShopSubscription = IDL.Record({
    id: IDL.Nat,
    shopNumber: IDL.Nat,
    created: IDL.Int,
    started: IDL.Int,
    expires: IDL.Int,
    durationDays: IDL.Nat,
    subscriptionCode: IDL.Opt(IDL.Text),
    mpesaRef: IDL.Opt(IDL.Text),
  });
  const ShopSubscriptionsPageResult = IDL.Variant({
    ok: IDL.Record({ items: IDL.Vec(ShopSubscription), nextCursor: IDL.Opt(IDL.Text) }),
    err: IndexError,
  });
  const RenewalForecast = IDL.Record({
    shops: IDL.Nat,
    amountKes: IDL.Nat,
  });
  const SubscriptionAdminStats = IDL.Record({
    subscribedShops: IDL.Nat,
    activeShops: IDL.Nat,
    expiredShops: IDL.Nat,
    renewals7Days: RenewalForecast,
    renewals14Days: RenewalForecast,
    renewals30Days: RenewalForecast,
    asOf: IDL.Int,
  });
  const SubscriptionAdminStatsResult = IDL.Variant({
    ok: SubscriptionAdminStats,
    err: IndexError,
  });
  const MpesaPaymentEvent = IDL.Record({
    transactionId: IDL.Text,
    transactionTime: IDL.Text,
    accountNumber: IDL.Opt(IDL.Text),
    amount: IDL.Nat,
    payer: IDL.Text,
  });
  const MpesaPaymentsPageResult = IDL.Variant({
    ok: IDL.Record({ items: IDL.Vec(MpesaPaymentEvent), nextCursor: IDL.Opt(IDL.Text) }),
    err: IndexError,
  });
  const MpesaPaymentResult = IDL.Variant({ ok: MpesaPaymentEvent, err: IndexError });
  const CanisterStatus = IDL.Record({
    cycles: IDL.Nat,
    idleCyclesBurnedPerDay: IDL.Nat,
    memorySize: IDL.Nat,
    status: IDL.Variant({
      running: IDL.Null,
      stopping: IDL.Null,
      stopped: IDL.Null,
    }),
  });
  const CanisterStatusResult = IDL.Variant({
    ok: CanisterStatus,
    err: IndexError,
  });
  const ShopHealth = IDL.Variant({
    Critical: IDL.Nat,
    Low: IDL.Nat,
    Healthy: IDL.Variant({ Infinite: IDL.Null, Days: IDL.Nat }),
  });
  const ShopStatus = IDL.Record({
    cycles: IDL.Nat,
    idleCyclesBurnedPerDay: IDL.Nat,
    memorySize: IDL.Nat,
    status: IDL.Variant({ running: IDL.Null, stopping: IDL.Null, stopped: IDL.Null }),
    health: ShopHealth,
    recordedAt: IDL.Int,
  });
  const ShopRecordWithStatus = IDL.Record({
    shop: ShopRecord,
    status: IDL.Opt(ShopStatus),
  });
  const ShopsWithStatusResult = IDL.Variant({
    ok: IDL.Vec(ShopRecordWithStatus),
    err: IndexError,
  });
  const PrincipalResult = IDL.Variant({ ok: IDL.Principal, err: IndexError });
  const UnitResult = IDL.Variant({ ok: IDL.Null, err: IndexError });
  const TextResult = IDL.Variant({ ok: IDL.Text, err: IndexError });
  const EtimsApiKeyUpdate = IDL.Variant({
    Unchanged: IDL.Null,
    Clear: IDL.Null,
    Set: IDL.Text,
  });
  const UpdateEtimsConfig = IDL.Record({
    enabled: IDL.Bool,
    apiKeyUpdate: EtimsApiKeyUpdate,
    businessPin: IDL.Opt(IDL.Text),
  });
  const EtimsConfigStatus = IDL.Record({
    enabled: IDL.Bool,
    businessPin: IDL.Opt(IDL.Text),
    apiKeyMasked: IDL.Opt(IDL.Text),
  });
  const EtimsConfigStatusResult = IDL.Variant({
    ok: EtimsConfigStatus,
    err: IndexError,
  });

  return IDL.Service({
    getMyAdminAccess: IDL.Func([], [AdminAccessResult], ["query"]),
    listAdmins: IDL.Func([], [AdminsResult], ["query"]),
    getAdmin: IDL.Func([IDL.Principal], [AdminResult], ["query"]),
    setSuperAdmin: IDL.Func([IDL.Principal], [UnitResult], []),
    addAdmin: IDL.Func([IDL.Principal, IDL.Text], [AdminResult], []),
    updateAdmin: IDL.Func([IDL.Principal, IDL.Text], [AdminResult], []),
    removeAdmin: IDL.Func([IDL.Principal], [UnitResult], []),
    listShops: IDL.Func([], [ShopsResult], ["query"]),
    listShopsWithStatus: IDL.Func([], [ShopsWithStatusResult], ["query"]),
    getShopId: IDL.Func([IDL.Nat], [PrincipalResult], ["query"]),
    getShopStats: IDL.Func([IDL.Nat], [ShopStatsResult], []),
    getEInvoiceRecord: IDL.Func([IDL.Nat, IDL.Nat], [EInvoiceRecordResult], []),
    getCanisterStatus: IDL.Func([IDL.Principal], [CanisterStatusResult], []),
    getShopDetail: IDL.Func(
      [IDL.Nat],
      [
        IDL.Variant({
          ok: IDL.Record({
            shop: ShopRecord,
            stats: ShopStats,
            status: IDL.Opt(ShopStatus),
          }),
          err: IndexError,
        }),
      ],
      [],
    ),
    setShopMpesaShortcode: IDL.Func(
      [IDL.Nat, IDL.Opt(MpesaShortcode)],
      [UnitResult],
      [],
    ),
    setShopEtimsConfig: IDL.Func(
      [IDL.Nat, UpdateEtimsConfig],
      [UnitResult],
      [],
    ),
    getShopEtimsConfig: IDL.Func([IDL.Nat], [EtimsConfigStatusResult], []),
    testShopEtimsConnection: IDL.Func([IDL.Nat], [TextResult], []),
    listPendingShops: IDL.Func([], [PendingShopsResult], ["query"]),
    approveShop: IDL.Func([IDL.Nat], [ShopRecordResult], []),
    createFreeRegistrationCode: IDL.Func(
      [NewFreeRegistrationCode],
      [FreeRegistrationCodeResult],
      [],
    ),
    listFreeRegistrationCodes: IDL.Func(
      [],
      [FreeRegistrationCodesResult],
      ["query"],
    ),
    deleteFreeRegistrationCode: IDL.Func([IDL.Text], [UnitResult], []),
    createFreeSubscriptionCode: IDL.Func(
      [NewFreeSubscriptionCode],
      [FreeSubscriptionCodeResult],
      [],
    ),
    listFreeSubscriptionCodes: IDL.Func(
      [],
      [FreeSubscriptionCodesResult],
      ["query"],
    ),
    deleteFreeSubscriptionCode: IDL.Func([IDL.Text], [UnitResult], []),
    listShopRegistrations: IDL.Func([IDL.Opt(IDL.Text), IDL.Nat], [ShopRegistrationsPageResult], ["query"]),
    listSubscriptions: IDL.Func([IDL.Opt(IDL.Text), IDL.Nat], [ShopSubscriptionsPageResult], ["query"]),
    getSubscriptionAdminStats: IDL.Func([], [SubscriptionAdminStatsResult], ["query"]),
    listShopSubscriptions: IDL.Func([IDL.Nat, IDL.Opt(IDL.Text), IDL.Nat], [ShopSubscriptionsPageResult], ["query"]),
    syncSubscriptionToShop: IDL.Func([IDL.Nat], [UnitResult], []),
    listMpesaPayments: IDL.Func([IDL.Opt(IDL.Text), IDL.Nat], [MpesaPaymentsPageResult], ["query"]),
    getMpesaPayment: IDL.Func([IDL.Text], [MpesaPaymentResult], ["query"]),
    ping: IDL.Func([], [IDL.Text], ["query"]),
  });
};

const INDEX_CANISTER_ID = import.meta.env.INDEX_CANISTER_ID;

function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function getAgentHost(): string {
  if (typeof window !== "undefined") {
    if (isLocalHost(window.location.hostname)) {
      const port = window.location.port || "4943";
      return `http://127.0.0.1:${port}`;
    }
    return window.location.origin;
  }
  return import.meta.env.DFX_NETWORK === "ic"
    ? "https://ic0.app"
    : "http://127.0.0.1:4943";
}

function getCanisterEnv(): Record<string, string> {
  if (typeof document === "undefined") return {};
  const raw = document.cookie
    .split("; ")
    .find((c) => c.startsWith("ic_env="))
    ?.slice("ic_env=".length);
  if (!raw) return {};
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("{")) {
      const parsed = JSON.parse(decoded);
      return parsed && typeof parsed === "object" ? parsed : {};
    }
    return Object.fromEntries(new URLSearchParams(decoded).entries());
  } catch {
    return {};
  }
}

function hexToArrayBuffer(hex: string): ArrayBuffer | undefined {
  if (!hex || hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) return undefined;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

async function makeAgent(identity: Identity): Promise<HttpAgent> {
  const env = getCanisterEnv();
  const rootKey = env.IC_ROOT_KEY || env.ic_root_key;
  return HttpAgent.create({
    identity,
    host: getAgentHost(),
    rootKey: rootKey ? hexToArrayBuffer(rootKey) : undefined,
  });
}

export function getIndexCanisterId(): string {
  const id =
    INDEX_CANISTER_ID || getCanisterEnv()["PUBLIC_CANISTER_ID:tillflo_index"];
  if (!id) throw new Error("Missing tillflo_index canister id");
  return id;
}

export function getFrontendCanisterId(): string {
  const id = getCanisterEnv()["PUBLIC_CANISTER_ID:tillflo_frontend"];
  if (!id) throw new Error("Missing tillflo_frontend canister id");
  return id;
}

export function getReportsCanisterId(): string {
  const id = getCanisterEnv()["PUBLIC_CANISTER_ID:tillflo_reports"];
  if (!id) throw new Error("Missing tillflo_reports canister id");
  return id;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createIndexActor(
  identity: Identity,
): Promise<ActorSubclass<any>> {
  const agent = await makeAgent(identity);
  return Actor.createActor(indexAdminIdl, {
    agent,
    canisterId: getIndexCanisterId(),
  });
}
